// The four contract stages Legal keeps, each with Legal's reason behind it, and Amazon's
// parent company guarantee where it applies. A project's Overview row and its Legal page
// both render them through this, so the two cannot disagree (principle 23). See
// docs/Y2026-Q3/design-dashboard-v2.md.
import type { ReactNode } from "react";

import type { AttributeDef, DashboardRow } from "../api/hooks/useDashboard";
import { formatDay, formatOptionValue } from "../utils/attributeFormat";
import { InfoToggle } from "./InfoToggle";
import styles from "./ContractStages.module.css";

const CONTRACTS: [string, string][] = [
  ["contract_roof_lease", "Roof lease"],
  ["contract_tenant_ppa", "Tenant PPA"],
  ["contract_epc", "EPC"],
  ["contract_offtaker", "Offtaker"],
];

const GUARANTEE = "contract_parent_guarantee";
const GUARANTEE_SIGNED = "contract_parent_guarantee_signed";

const Line = ({
  label,
  stage,
  note,
}: {
  label: string;
  stage: ReactNode;
  note: ReactNode;
}) => (
  <span className={styles.line}>
    <span className={styles.key}>{label}</span>
    <span className={styles.stageValue}>
      {note ? (
        <InfoToggle label={`Why ${label}`} trigger={stage}>
          {note}
        </InfoToggle>
      ) : (
        stage
      )}
    </span>
  </span>
);

// Only on a project the guarantee covers: Legal leaves it unset elsewhere.
const ParentGuarantee = ({
  row,
  definitions,
}: {
  row: DashboardRow;
  definitions: Map<string, AttributeDef>;
}) => {
  const definition = definitions.get(GUARANTEE);
  const value = row.attributes[GUARANTEE];
  if (!definition || !value) return null;
  const stages = value.options.map((o) => o.value);
  if (stages.every((stage) => stage === "not_applicable")) return null;
  const signed = row.attributes[GUARANTEE_SIGNED]?.options
    .map((o) => String(o.value ?? ""))
    .filter(Boolean)
    .sort()[0];
  const stage = (
    <>
      {stages.map((v) => formatOptionValue(definition, v)).join(" or ")}
      {stages.includes("delivered") && (
        <span className={styles.small}>
          {signed ? ` (signed ${formatDay(signed)})` : " (signing date not recorded)"}
        </span>
      )}
    </>
  );
  return (
    <Line
      label="Parent guarantee"
      stage={stage}
      note={
        <>
          {value.reason && <span className={styles.note}>{value.reason}</span>}
          <span className={styles.note}>{definition.describe}</span>
        </>
      }
    />
  );
};

export const ContractStages = ({
  row,
  definitions,
}: {
  row: DashboardRow;
  definitions: Map<string, AttributeDef>;
}) => (
  <>
    {CONTRACTS.map(([key, label]) => {
      const definition = definitions.get(key);
      const value = row.attributes[key];
      const stage = (
        <>
          {definition && value ? (
            value.options
              .map((o) => formatOptionValue(definition, o.value))
              .join(" or ")
          ) : (
            <span className={styles.muted}>not set</span>
          )}
          {key === "contract_epc" && row.epc_party && (
            <span className={styles.small}> ({row.epc_party})</span>
          )}
        </>
      );
      return <Line key={key} label={label} stage={stage} note={value?.reason} />;
    })}
    <ParentGuarantee row={row} definitions={definitions} />
  </>
);
